import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Backdrop,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import base_url from "../../../constants";

const CARD_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: 820,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  p: 4,
};

const WhatsappBusinessGroupDeleteGroupModal = ({
  open,
  onClose,
  groupName,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const notify = useNotification();

  useEffect(() => {
    if (!open) {
      // reset state on close if needed
    }
  }, [open]);

  const handleDelete = async () => {
    if (!groupName) {
      notify("Group name is missing. Please try again.", "error");
      return;
    }

    try {
      setLoading(true);
      const encodedGroupName = encodeURIComponent(groupName);
      const res = await axiosInstance.delete(
        `${base_url}/whatsapp-business/groups/${encodedGroupName}`
      );

      if (res.data?.status === "success" || res.data?.success) {
        notify(res.data?.message || "Group deleted successfully", "success");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        notify(res.data?.message || "Failed to delete group", "error");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        notify(
          err.response?.data?.message || "Group not found",
          "error"
        );
      } else {
        notify(
          err.response?.data?.message ||
            "Something went wrong while deleting group.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="delete-group-modal-title"
      aria-describedby="delete-group-modal-description"
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(15,23,42,0.55)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={CARD_STYLE}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Delete Group
                    </Typography>
                    <Button
                      onClick={onClose}
                      size="small"
                      sx={{ minWidth: "auto" }}
                    >
                      ✕
                    </Button>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    This will permanently delete{" "}
                    <strong>{groupName || "this group"}</strong> and all its
                    contacts. This action cannot be undone.
                  </Typography>

                  {groupName && (
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mr: 0.5 }}
                      >
                        Group:
                      </Typography>
                      <Chip
                        size="small"
                        label={groupName}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="error"
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        position: "relative",
                      }}
                      disabled={loading}
                      onClick={handleDelete}
                    >
                      Delete Group
                      {loading ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      ) : null}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default WhatsappBusinessGroupDeleteGroupModal;

